import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Palette, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * DesignerServicePanel
 * Sits on the Order Detail page (Artwork tab). Fetches design-service
 * requests for this order, shows a call-to-action if none exist, and
 * renders status + change-request UI once one is in progress.
 *
 * Small footprint by design — the heavier queue lives on /designer-queue.
 */
export function DesignerServicePanel({ orderId, token, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [fee, setFee] = useState(25);
  const [amendmentFee, setAmendmentFee] = useState(25);
  const [freeChanges, setFreeChanges] = useState(2);
  const [internal, setInternal] = useState(false);
  const [showChangesDialog, setShowChangesDialog] = useState(null); // holds request obj
  const [changeNotes, setChangeNotes] = useState('');

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/designer-service/for-order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(data.items || []);
      setInternal(!!data.internal);
      setFee(data.internal ? 0 : (Number(data.artwork_fee ?? data.fee_amount) || 25));
      setAmendmentFee(data.internal ? 0 : (Number(data.amendment_fee ?? data.artwork_fee ?? data.fee_amount) || 25));
      setFreeChanges(Number(data.free_changes) || 2);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [orderId]);

  // Auto-verify payment after Stripe redirect
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get('designer');
    const sessionId = p.get('session_id');
    if (status === 'paid' && sessionId) {
      axios.post(`${API}/designer-service/verify-payment`, { session_id: sessionId }, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(() => {
        toast.success('Payment received — the designer has been notified.');
        window.history.replaceState({}, '', window.location.pathname);
        refresh();
      }).catch(() => toast.error('Could not verify payment yet — check back shortly.'));
    } else if (status === 'cancelled') {
      toast.info('Design service payment cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token, orderId]);

  const startRequest = async () => {
    if (!window.confirm(
      internal
        ? `Request the platform designer to create the artwork for this order?\n\nThis is free for your account (internal use). The designer will be notified straight away.`
        : `Request the platform designer to create the artwork for this order?\n\n` +
          `A one-off fee of £${fee.toFixed(2)} applies. You'll get ${freeChanges} free change request${freeChanges === 1 ? '' : 's'} included. ` +
          `Additional changes after that will cost £${amendmentFee.toFixed(2)} each.`
    )) return;
    try {
      const { data } = await axios.post(`${API}/designer-service/${orderId}/request`, {
        notes: null,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success('Artwork requested — the designer has been notified. No charge (internal use).');
        refresh();
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to start design service request';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start');
    }
  };

  const submitChanges = async () => {
    if (!changeNotes.trim()) return toast.error('Please describe what needs changing.');
    const req = showChangesDialog;
    const used = req.changes_used || 0;
    const limit = req.changes_free_limit || freeChanges;
    // Client-side warn before hitting a chargeable change
    if (used >= limit && !internal) {
      if (!window.confirm(
        `You've used all free changes (${used}/${limit}).\n\n` +
        `This next change is chargeable at £${amendmentFee.toFixed(2)}. Continue to payment?`
      )) return;
    }
    try {
      const { data } = await axios.post(`${API}/designer-service/${req.id}/request-changes`,
        { notes: changeNotes.trim(), origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } });
      setShowChangesDialog(null);
      setChangeNotes('');
      if (data.requires_payment && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(`Change request sent (${data.changes_remaining_free} free change${data.changes_remaining_free === 1 ? '' : 's'} left).`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit changes');
    }
  };

  if (loading) return null;

  const active = requests.find(r => r.status !== 'delivered');
  const delivered = requests.filter(r => r.status === 'delivered');

  return (
    <>
      {/* Call to action when nothing in flight */}
      {!active && delivered.length === 0 && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 mb-4">
          <CardContent className="p-4 flex items-start gap-3">
            <Palette className="w-10 h-10 text-purple-600 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-purple-900">Need artwork done fast?</div>
              <p className="text-xs text-purple-800 mt-1">
                {internal
                  ? `Our platform designer can create the memorial artwork for you and upload it directly to this order — free for your account (internal use), with unlimited change requests.`
                  : `Our platform designer can create the memorial artwork for you and upload it directly to this order — one-off fee £${fee.toFixed(2)}, ${freeChanges} free change request${freeChanges === 1 ? '' : 's'} included. Extra changes £${amendmentFee.toFixed(2)} each.`}
              </p>
            </div>
            <Button size="sm" onClick={startRequest} className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
                    data-testid="request-designer-service">
              <Sparkles className="w-4 h-4 mr-1" /> {internal ? 'Request Design (Free)' : `Request Design (£${fee.toFixed(2)})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Status of in-flight request */}
      {active && (
        <Card className="border-amber-300 bg-amber-50 mb-4">
          <CardContent className="p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-amber-900">
              <Palette className="w-4 h-4" /> Design service — status: {active.status.replace('_', ' ')}
            </div>
            <p className="text-xs text-amber-800 mt-1">
              {active.status === 'awaiting_payment' && 'Waiting for payment to complete.'}
              {active.status === 'paid' && 'Payment received — waiting for the designer to start.'}
              {active.status === 'changes_requested' && `Change request sent to the designer (${active.changes_used}/${active.changes_free_limit} free used).`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delivered — offer change requests */}
      {delivered.map(r => {
        const limit = r.changes_free_limit ?? freeChanges;
        const remaining = Math.max(0, limit - (r.changes_used || 0));
        return (
          <Card key={r.id} className="border-emerald-300 bg-emerald-50 mb-4">
            <CardContent className="p-3 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="font-medium text-emerald-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Designer artwork delivered
                </div>
                <p className="text-xs text-emerald-800 mt-1">
                  {remaining > 0
                    ? `${remaining} free change request${remaining === 1 ? '' : 's'} remaining.`
                    : `Free change allowance used (${limit}/${limit}). Further changes cost £${amendmentFee.toFixed(2)} each.`}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setShowChangesDialog(r); setChangeNotes(''); }}
                      data-testid={`request-designer-changes-${r.id}`}>
                Request changes
              </Button>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!showChangesDialog} onOpenChange={(o) => !o && setShowChangesDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request changes to the artwork</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-600">
              Describe exactly what should change. Please be specific — this will be sent straight to the designer.
            </p>
            {showChangesDialog && (
              <p className="text-xs text-amber-700">
                {(showChangesDialog.changes_used || 0) < (showChangesDialog.changes_free_limit ?? freeChanges)
                  ? `Free (${(showChangesDialog.changes_used || 0) + 1} of ${showChangesDialog.changes_free_limit ?? freeChanges}).`
                  : `⚠ Chargeable — a £${amendmentFee.toFixed(2)} fee will apply.`}
              </p>
            )}
            <Textarea rows={6} value={changeNotes} onChange={(e) => setChangeNotes(e.target.value)}
                      placeholder="e.g. Change the rose to a lily, bold the deceased name…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangesDialog(null)}>Cancel</Button>
            <Button onClick={submitChanges} className="bg-purple-600 hover:bg-purple-700">
              Send to designer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * DesignerQueueBadge — small red pill for the designer's nav button
 * showing pending job count. Refreshes every 60s.
 */
export function DesignerQueueBadge({ token, currentUser }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const isDesigner = currentUser?.email === 'memorials@setinstonedidcot.co.uk' || currentUser?.role === 'super_admin';
    if (!isDesigner) return;
    const fetchIt = async () => {
      try {
        const { data } = await axios.get(`${API}/designer-service/queue`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setCount(data.pending_count || 0);
      } catch { /* silent */ }
    };
    fetchIt();
    const iv = setInterval(fetchIt, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, [token, currentUser?.email, currentUser?.role]);
  if (count <= 0) return null;
  return (
    <span
      className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-purple-600 text-white text-[10px] font-bold leading-none ring-2 ring-white animate-pulse"
      title={`${count} design-service job${count === 1 ? '' : 's'} in your queue`}
      data-testid="designer-queue-badge"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}
